import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { SERVICE_OPTIONS, INDUSTRY_OPTIONS } from "@/data/site";

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service_interest: "",
  industry: "",
  message: "",
};

export default function LeadForm({ compact = false, source = "website" }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in name, work email and company.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source });
      toast.success("Thanks! A growth strategist will reach out within 24 hours.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="lead-form"
      onSubmit={onSubmit}
      className={`bg-white border border-slate-200 rounded-md p-6 md:p-8 shadow-sm ${compact ? "" : "lg:p-10"}`}
    >
      <div className="mb-6">
        <p className="eyebrow">Lead Capture</p>
        <h3 className="font-display text-2xl md:text-3xl text-[#0A1F44] mt-2 tracking-tight">
          Tell us about your revenue goals.
        </h3>
        <p className="text-sm text-slate-600 mt-2">
          A growth strategist will respond within 24 hours.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full Name *">
          <input
            data-testid="lead-input-name"
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            className="form-input"
            placeholder="Jane Doe"
          />
        </Field>
        <Field label="Work Email *">
          <input
            data-testid="lead-input-email"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            className="form-input"
            placeholder="jane@company.com"
          />
        </Field>
        <Field label="Company *">
          <input
            data-testid="lead-input-company"
            type="text"
            required
            value={form.company}
            onChange={update("company")}
            className="form-input"
            placeholder="Company Inc."
          />
        </Field>
        <Field label="Phone">
          <input
            data-testid="lead-input-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            className="form-input"
            placeholder="+1 555 000 0000"
          />
        </Field>
        <Field label="Service of Interest">
          <select
            data-testid="lead-input-service"
            value={form.service_interest}
            onChange={update("service_interest")}
            className="form-input"
          >
            <option value="">Select a service</option>
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Industry">
          <select
            data-testid="lead-input-industry"
            value={form.industry}
            onChange={update("industry")}
            className="form-input"
          >
            <option value="">Select an industry</option>
            {INDUSTRY_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="What are you trying to solve?">
            <textarea
              data-testid="lead-input-message"
              value={form.message}
              onChange={update("message")}
              rows={4}
              className="form-input resize-none"
              placeholder="Briefly describe your revenue or expansion goals."
            />
          </Field>
        </div>
      </div>

      <button
        data-testid="lead-submit"
        type="submit"
        disabled={loading}
        className="btn-primary mt-6 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {loading ? "Submitting…" : "Book a Growth Consultation"}
      </button>

      <p className="text-xs text-slate-500 mt-4">
        We respect your privacy. Your information is used only to contact you regarding your enquiry.
      </p>

      <style>{`
        .form-input {
          width: 100%;
          background: #fff;
          border: 1px solid #E2E8F0;
          color: #0F172A;
          padding: 0.7rem 0.9rem;
          font-size: 0.92rem;
          border-radius: 4px;
          font-family: 'Open Sans', sans-serif;
          transition: border-color .2s, box-shadow .2s;
        }
        .form-input:focus {
          outline: none;
          border-color: #1E40AF;
          box-shadow: 0 0 0 3px rgba(30,64,175,0.12);
        }
        .form-input::placeholder { color: #94a3b8; }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
